/*
Copyright (c) 2012-2026 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import React, { useLayoutEffect, useRef, useState } from "react"

import styles from "./ShowMore.modules.css"

type ShowMoreProp = {
  text: string
  moreLabel: string
  lessLabel: string
  /** Number of lines to show while collapsed. */
  lines?: number
}

/**
 * Collapses long text to a fixed number of lines using a CSS line clamp, with a
 * toggle to expand it.
 *
 * The DOM shape here is deliberate, not incidental. The text is rendered as a
 * single string child, so React sets it with `textContent` and never creates a
 * child node it might later remove; expanding only swaps a class name; and the
 * toggle is always mounted, hidden via the `hidden` attribute rather than being
 * added and removed. Browser auto-translate (Edge in particular) reparents
 * inline runs under its own wrapper elements, which leaves React holding stale
 * parent references — so any child React might later remove or insert is a
 * crash waiting to happen.
 *
 * This replaced `react-show-more`, which measured text and emitted one <span>
 * per line: dropping those spans when the line count shrank threw
 * `NotFoundError: Failed to execute 'removeChild' on 'Node'` on translated
 * pages and took down the whole screen. Do not reintroduce per-line nodes or
 * conditionally rendered children here.
 */
const ShowMore: React.FC<ShowMoreProp> = ({
  text,
  moreLabel,
  lessLabel,
  lines = 4,
}) => {
  const textRef = useRef<HTMLDivElement>(null)
  const [expanded, setExpanded] = useState(false)
  const [isClamped, setIsClamped] = useState(false)

  useLayoutEffect(() => {
    const node = textRef.current
    if (!node) return undefined

    node.style.setProperty("--show-more-lines", String(lines))

    // While expanded the clamp is off, so there is nothing to measure — the
    // existing result stays until the text collapses again.
    if (expanded) return undefined

    const measure = () => {
      setIsClamped(node.scrollHeight > node.clientHeight)
    }
    measure()

    const disconnects: Array<() => void> = []

    if (typeof window !== "undefined" && window.ResizeObserver) {
      const resizeObserver = new window.ResizeObserver(measure)
      resizeObserver.observe(node)
      disconnects.push(() => resizeObserver.disconnect())
    }

    // A resize observer is not enough on its own: while clamped, the element's
    // height is pinned to the clamp, so text that is rewritten in place —
    // browser auto-translate being the common case — changes scrollHeight
    // without changing the observed box, and the toggle would be left showing
    // for text that now fits, or hidden for text that now overflows.
    if (typeof window !== "undefined" && window.MutationObserver) {
      const mutationObserver = new window.MutationObserver(measure)
      mutationObserver.observe(node, {
        characterData: true,
        childList: true,
        subtree: true,
      })
      disconnects.push(() => mutationObserver.disconnect())
    }

    return () => disconnects.forEach(disconnect => disconnect())
  }, [text, expanded, lines])

  return (
    <React.Fragment>
      <div
        ref={textRef}
        className={expanded ? styles.text : `${styles.text} ${styles.clamped}`}
        data-testid="show-more-text"
      >
        {text}
      </div>
      <button
        type="button"
        hidden={!isClamped}
        className={styles.toggle}
        aria-expanded={expanded}
        data-testid="show-more-toggle"
        onClick={() => setExpanded(current => !current)}
      >
        {expanded ? lessLabel : moreLabel}
      </button>
    </React.Fragment>
  )
}

export default ShowMore
